import { useCallback, useRef, useState } from 'react';
import { toPng } from 'html-to-image';

/**
 * Custom hook for exporting a container element as PNG using html-to-image
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.title - Title used for the filename
 * @param {string} options.backgroundColor - Background color for the exported image
 * @param {React.RefObject} options.containerRef - External ref for the container to export
 * @returns {Object} Export functionality, loading state, and ref
 */
export const useChartExport = ({ 
  title = 'chart',
  backgroundColor = '#ffffff',
  containerRef: externalContainerRef = null,
}) => {
  const internalContainerRef = useRef(null);
  const [isExporting, setIsExporting] = useState(false);
  
  const finalContainerRef = externalContainerRef || internalContainerRef;
  
  // Define filter function for html-to-image
  const filter = (node) => {
    if (node instanceof HTMLElement) {
      return node.getAttribute('data-html2canvas-ignore') !== 'true';
    }
    return true;
  };

  // Renamed to indicate it's the core export action
  const triggerExport = useCallback(async () => {
    console.log('Triggering export for element:', finalContainerRef.current);

    if (!finalContainerRef.current) {
      console.error('Container ref is not attached to an element for export.');
      return false; // Indicate failure
    }
    
    setIsExporting(true);
    console.log('Starting html-to-image generation...');

    try {
      // No need to wait here anymore, parent component handles timing
      const dataUrl = await toPng(finalContainerRef.current, { 
        backgroundColor, 
        filter,
        cacheBust: true,
      });

      console.log('PNG generated successfully, preparing download...');
      
      const date = new Date();
      const formattedDate = date.toISOString().split('T')[0];
      const formattedTime = date.toTimeString().split(' ')[0].replace(/:/g, '-');
      
      const downloadLink = document.createElement('a');
      downloadLink.href = dataUrl;
      downloadLink.download = `${title || 'chart'}_${formattedDate}_${formattedTime}.png`;
      downloadLink.click();
      
      console.log('Download triggered successfully');
      return true; // Indicate success

    } catch (error) {
      console.error('Error during html-to-image export:', error);
      return false; // Indicate failure
    } finally {
      // Only reset loading state here
      setIsExporting(false);
    }
  }, [title, backgroundColor, finalContainerRef]); // Removed setters from dependencies

  return {
    triggerExport, // Return the renamed function
    containerRef: finalContainerRef,
    isExporting
  };
}; 